import { apiClient } from './client'
import type { ApiResponse } from '@/types/api'

export interface LcContentFeePlan {
  id: number
  contentId: number
  contentTitle: string | null
  level: string
  price: number
  currency: string
  grantedLessons: number
  active: boolean
}

export interface UpsertPlanRequest {
  contentId: number
  level: string
  price: number
  grantedLessons: number
  active: boolean
}

export interface LcContentFeePlanTemplateTier {
  id?: number | null
  level: string
  price: number
  currency: string
  grantedLessons: number
  active: boolean
}

export interface LcContentFeePlanTemplate {
  id: number
  name: string
  description: string | null
  active: boolean
  tiers: LcContentFeePlanTemplateTier[]
}

export interface SavePlanTemplateRequest {
  name: string
  description?: string | null
  active: boolean
  tiers: LcContentFeePlanTemplateTier[]
}

export interface ApplyPlanTemplateRequest {
  contentId: number
  levels: string[]
}

export interface PlanTemplateLevelComparison {
  level: string
  before: LcContentFeePlan | null
  after: LcContentFeePlanTemplateTier
}

export interface PlanTemplateApplyPreview {
  templateId: number
  templateName: string
  contentId: number
  contentTitle: string | null
  levels: string[]
  changes: PlanTemplateLevelComparison[]
  courseOverrideWarning: boolean
  zeroValueWarning: boolean
}

export interface EffectivePlanTier {
  level: string
  price: number
  currency: string
  grantedLessons: number
}

export type EffectivePlanSource = 'COURSE' | 'CURRICULUM' | 'NONE'

export interface EffectivePlanCourse {
  courseId: number
  title: string
  /** COURSE = 코스 예외, CURRICULUM = 과정 기본값 사용, NONE = 적용 플랜 없음 */
  source: EffectivePlanSource
  tiers: EffectivePlanTier[]
  missingLevels: string[]
  sameAsCurriculum: boolean
}

export interface EffectivePlanCurriculum {
  curriculumId: number
  title: string
  tiers: EffectivePlanTier[]
  courses: EffectivePlanCourse[]
}

export interface AdminTestLearnerCandidate {
  userId: number
  fullName: string
  email: string
  organizationId: number
  organizationName: string
  // backend는 null 필드를 응답에서 생략하므로 아래 선택 필드는 없을(undefined) 수 있다.
  /** 원생의 현재 과정에서 판정한 플랜 콘텐츠. 판정할 수 없으면 비어 있다. */
  contentId?: number | null
  contentTitle?: string | null
  plans: AdminTestPlanOption[]
  unavailableReason?: string | null
  activeEntitlementId?: number | null
  activeEntitlementSource?: 'PURCHASE' | 'ADMIN_TEST' | null
}

export interface AdminTestPlanOption {
  level: string
  grantedLessons: number
}

export interface AdminTestEntitlement {
  entitlementId: number
  userId: number
  fullName: string
  email: string
  organizationId: number
  organizationName: string
  contentId: number
  contentTitle: string | null
  level: string
  grantedLessons: number
  usedLessons: number
  remainingLessons: number
  status: string
  source: 'ADMIN_TEST'
  grantReason: string
  adminGrantExpiresOn: string
  grantedByUserId: number
  createdAt: string
  revokedByUserId: number | null
  revokedAt: string | null
  revokeReason: string | null
}

export interface GrantAdminTestEntitlementRequest {
  organizationId: number
  userId: number
  contentId: number
  level: string
  grantAttemptId: string
  grantReason: string
  confirmationText: string
}

export interface OrganizationContentFeeOrder {
  entitlementId: number
  beneficiaryUserId: number
  beneficiaryName: string | null
  beneficiaryEmail: string | null
  level: string
  contentTitle: string | null
  grantedLessons: number
  carriedOver: number
  usedLessons: number
  remainingLessons: number
  amount: number | null
  status: string
  paidAt: string | null
  refundedAt: string | null
  orderId: number | null
  pgProvider: string | null
  pgTransactionId: string | null
  cardLast4Digits: string | null
}

export interface RefundOrganizationContentFeeRequest {
  memo: string | null
}

const BASE = '/admin/lc/content-fee'
const TEST_ENTITLEMENTS_BASE = `${BASE}/test-entitlements`

export const lcContentFeeApi = {
  getEffectivePlans: () =>
    apiClient.get<ApiResponse<EffectivePlanCurriculum[]>>(`${BASE}/effective-plans`),
  getPlans: (contentId?: number) =>
    apiClient.get<ApiResponse<LcContentFeePlan[]>>(`${BASE}/plans`, {
      params: contentId ? { contentId } : undefined,
    }),
  upsertPlan: (body: UpsertPlanRequest) =>
    apiClient.put<ApiResponse<LcContentFeePlan>>(`${BASE}/plans`, body),
  deletePlan: (id: number) =>
    apiClient.delete<ApiResponse<void>>(`${BASE}/plans/${id}`),
  getTemplates: () =>
    apiClient.get<ApiResponse<LcContentFeePlanTemplate[]>>(`${BASE}/templates`),
  getTemplate: (id: number) =>
    apiClient.get<ApiResponse<LcContentFeePlanTemplate>>(`${BASE}/templates/${id}`),
  createTemplate: (body: SavePlanTemplateRequest) =>
    apiClient.post<ApiResponse<LcContentFeePlanTemplate>>(`${BASE}/templates`, body),
  updateTemplate: (id: number, body: SavePlanTemplateRequest) =>
    apiClient.put<ApiResponse<LcContentFeePlanTemplate>>(`${BASE}/templates/${id}`, body),
  deleteTemplate: (id: number) =>
    apiClient.delete<ApiResponse<void>>(`${BASE}/templates/${id}`),
  previewTemplateApply: (templateId: number, body: ApplyPlanTemplateRequest) =>
    apiClient.post<ApiResponse<PlanTemplateApplyPreview>>(`${BASE}/templates/${templateId}/preview`, body),
  applyTemplate: (templateId: number, body: ApplyPlanTemplateRequest) =>
    apiClient.post<ApiResponse<PlanTemplateApplyPreview>>(`${BASE}/templates/${templateId}/apply`, body),
  searchTestLearners: (keyword: string) =>
    apiClient.get<ApiResponse<AdminTestLearnerCandidate[]>>(`${TEST_ENTITLEMENTS_BASE}/learners`, {
      params: { keyword },
    }),
  getActiveTestEntitlements: () =>
    apiClient.get<ApiResponse<AdminTestEntitlement[]>>(TEST_ENTITLEMENTS_BASE),
  grantTestEntitlement: (body: GrantAdminTestEntitlementRequest) =>
    apiClient.post<ApiResponse<AdminTestEntitlement>>(`${TEST_ENTITLEMENTS_BASE}/grant`, body),
  revokeTestEntitlement: (entitlementId: number, reason: string) =>
    apiClient.post<ApiResponse<AdminTestEntitlement>>(
      `${TEST_ENTITLEMENTS_BASE}/${entitlementId}/revoke`,
      { reason },
    ),
  getOrganizationOrders: (organizationId: number) =>
    apiClient.get<ApiResponse<OrganizationContentFeeOrder[]>>(
      `/admin/lc/organizations/${organizationId}/content-fee/orders`,
    ),
  refundOrganizationOrder: (
    organizationId: number,
    entitlementId: number,
    body: RefundOrganizationContentFeeRequest,
  ) =>
    apiClient.post<ApiResponse<void>>(
      `/admin/lc/organizations/${organizationId}/content-fee/entitlements/${entitlementId}/refund`,
      body,
    ),
}

export const CONTENT_FEE_LEVELS = ['L3', 'L4', 'L5'] as const
